import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Warning, Wrench } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = [
  { v: "mecanico", l: "Mecánico" },
  { v: "electrico", l: "Eléctrico" },
  { v: "quimico", l: "Químico" },
  { v: "biologico", l: "Biológico" },
  { v: "ergonomico", l: "Ergonómico" },
  { v: "psicosocial", l: "Psicosocial" },
  { v: "fisico", l: "Físico" },
  { v: "ambiental", l: "Ambiental" },
];

const empty = () => ({ name: "", category: "mecanico", description: "", typical_severity: 3, active: true });

/**
 * HazardsPanel — administración del catálogo de peligros que alimenta el
 * dropdown del IPER-C. Se muestra dentro de Ajustes → Riesgos.
 */
export default function HazardsPanel({ canManage }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty());
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/hse/hazards-catalog").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing) await api.put(`/hse/hazards-catalog/${editing}`, form);
      else await api.post("/hse/hazards-catalog", form);
      toast.success("Peligro guardado"); setForm(empty()); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar peligro del catálogo?")) return;
    try { await api.delete(`/hse/hazards-catalog/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const openEdit = (it) => { setEditing(it.id); setForm({ ...empty(), ...it }); };

  const byCat = items.reduce((acc, it) => {
    const k = it.category || "otros";
    (acc[k] = acc[k] || []).push(it); return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="hazards-panel">
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Warning size={18} weight="fill" className="text-red-600" />
          <div>
            <h3 className="font-heading font-bold text-slate-900">Catálogo de peligros (IPER-C)</h3>
            <p className="text-xs text-slate-500">Alimenta el dropdown de peligros en IPER-C. Organiza por categoría según GTC 45 / NOM-030.</p>
          </div>
        </div>

        {canManage && (
          <div className="grid grid-cols-12 gap-2 items-end bg-slate-50 rounded p-3 mb-3">
            <div className="col-span-4"><Label className="overline-label">Nombre</Label>
              <Input data-testid="hazard-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Caída de objetos desde altura" /></div>
            <div className="col-span-2"><Label className="overline-label">Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="hazard-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-4"><Label className="overline-label">Descripción</Label>
              <Input data-testid="hazard-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Detalle o ejemplo típico" /></div>
            <div className="col-span-1"><Label className="overline-label">Sev.</Label>
              <Input type="number" min={1} max={5} data-testid="hazard-severity" value={form.typical_severity} onChange={(e) => setForm({ ...form, typical_severity: Number(e.target.value) || 3 })} /></div>
            <div className="col-span-1"><Button data-testid="hazard-save" onClick={save} className="w-full bg-red-700 hover:bg-red-800">{editing ? "Actualizar" : "Añadir"}</Button></div>
          </div>
        )}

        {Object.keys(byCat).sort().map((cat) => {
          const label = CATEGORIES.find((c) => c.v === cat)?.l || cat;
          return (
            <div key={cat} className="mb-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-red-700 mb-1">{label} ({byCat[cat].length})</p>
              <div className="space-y-1">
                {byCat[cat].map((h) => (
                  <div key={h.id} data-testid={`hazard-row-${h.id}`} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                    <span className="font-semibold text-slate-900 flex-1 truncate">{h.name}</span>
                    <span className="text-[10px] text-slate-500 truncate">{h.description || "—"}</span>
                    <span className="text-[10px] font-mono px-1 py-0.5 bg-red-100 text-red-700 rounded">Sev {h.typical_severity}</span>
                    {!h.active && <span className="text-[10px] px-1 py-0.5 bg-slate-200 text-slate-600 rounded">inactivo</span>}
                    {canManage && <>
                      <button onClick={() => openEdit(h)} className="text-slate-400 hover:text-blue-700" data-testid={`hazard-edit-${h.id}`}><PencilSimple size={12} /></button>
                      <button onClick={() => remove(h.id)} className="text-slate-400 hover:text-red-600" data-testid={`hazard-delete-${h.id}`}><Trash size={12} /></button>
                    </>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-xs text-slate-400 italic text-center py-4 flex items-center justify-center gap-1"><Wrench size={12} /> Sin peligros catalogados aún.</p>
        )}
      </div>
    </div>
  );
}
